import React, { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Grid, Box, Button, FormControl, CircularProgress, Typography, TextField, IconButton, Icon, Stack, useMediaQuery, Link, Snackbar, Alert } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import AddPlusIcon from '../../icons/AddPlusIcon.svg';
import EditPinIcon from '../../icons/EditPinIcon.svg';
import { updateUserDetails, selectAuthState, clearUserDetailsResult } from '../sagas/auth/authSlice';
import { Role, UpdateUserDetailsDto } from '@inosio/payments-sdk';
import ResetLoginInfoModal from './ResetLoginInfo';
import { E164PhoneRegex} from '../../utils/regex';
import { getSupportedNotifications, selectNotificationSettingsState } from '../notificationSettings/sagas/notificationSettingSlice';
import { NotificationTab } from '../notificationSettings/components/notificationTab';
import { PhoneNumberInput } from '../PhoneNumberInput';
// import encodeQR from '@paulmillr/qr';

const useStyles = makeStyles({
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '48px',
    backgroundColor: '#053742',
    padding: '0 13px',
    borderRadius: '25px 25px 0px 0px',
  },
  bodyContainer: {
    border: '2px solid #F1F0F5',
    borderRadius: '0px 0px 25px 25px',
    minHeight: '158px',
  },
  inputStyle: {
    borderRadius: '24px !important',
    outline: 'none !important',
    fontFamily: 'Work Sans !important',
    height: '32px  !important',
    width: '100%',
    maxWidth: '330px',
    '& fieldset legend span': {
      fontSize: '9px !important',
    },
  },
  disabledInputContainer: {
    height: '32px  !important',
    width: '100%',
    maxWidth: '330px',
    border: '1px solid #F1F0F5',
    backgroundColor: '#F1F0F5',
    borderRadius: '24px',
  },
  placeholderStyle: {
    fontFamily: 'Work Sans !important',
    top: '-11px !important',
    '&[data-shrink="true"]': {
      top: '0px !important',
      fontSize: '12px !important',
      color: '#000000 !important',
      lineHeight: '12px !important',
    },
  },
  loginFieldContainer: {
    border: '1px solid #F1F0F5',
    borderRadius: '24px',
    height: '32px',
    width: 'calc( 100% - 50px )',
    maxWidth: '430px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 45px 0 15px',
    position: 'relative',
  },
  disabledLoginFieldContainer: {
    border: '1px solid #F1F0F5',
    backgroundColor: '#F1F0F5',
    borderRadius: '24px',
    height: '32px',
    width: 'calc( 100% - 50px )',
    maxWidth: '430px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 45px 0 15px',
    position: 'relative',
  },
  saveButtonStyle: {
    background: '#39A2DB !important',
    borderRadius: '24px !important',
    color: '#fff !important',
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    fontSize: '18px !important',
    lineHeight: '22px !important',
    height: '31px !important',
    '&:hover': {
      background: '#226183 !important',
    },
    '&:disabled': {
      background: '#D3D3D3 !important',
      color: '#fff !important',
    },
  },
});

export enum ModalTypes {
  Email = 'Email',
  Password = 'Password',
}

export default function ProfileSettings() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const matchesMobile = useMediaQuery('(max-width:600px)');
  const matchesIpad = useMediaQuery('(max-width:900px)');

  //selectors
  const { userDetails, isUpdateUserDetailsSuccess, updateUserDetailsError, ipaUser } = useSelector(selectAuthState);
  const currentPrincipal = ipaUser?.currentProfile;
  const { supportedNotifications, isCreatingNotificationSubscription, isCreatingNotificationSubscriptionFailed } = useSelector(selectNotificationSettingsState);
  // const toptQr = encodeQR("url here", "svg", { ecc: "medium", scale: 1, border: 1 });


  //local states
  const [showToast, setShowToast] = React.useState(false);
  const [firstName, setFirstName] = useState<undefined | string>('');
  const [middleName, setMiddleName] = useState<undefined | string>('');
  const [lastName, setLastName] = useState<undefined | string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [phoneError, setPhoneError] = useState('');
  const [isInvalidGeneralInfo, setIsInvalidGeneralInfo] = useState(false);
  const [isUpdatedGeneralInfo, setIsUpdatedGeneralInfo] = useState(false);
  const [isUpdatingGeneralInfo, setIsUpdatingGeneralInfo] = useState(false);
  const [isRegisteredUser, setIsRegisteredUser] = useState(false);
  const [email, setEmail] = useState('');
  const [defaultPrincipalUuid, setDefaultPrincipalUuid] = useState<undefined | string>('');
  const [isUpdatedPrincipal, setIsUpdatedPrincipal] = useState(false);
  const [openResetLoginInfoModal, setOpenResetLoginInfoModal] = useState(false);
  const [modalType, setModalType] = useState(ModalTypes.Email);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if ((currentPrincipal?.role === Role.Owner) && !isCreatingNotificationSubscription) {
      dispatch({ type: getSupportedNotifications.type });
    }
  }, [isCreatingNotificationSubscription]);

  useEffect(() => {
    setShowToast(isCreatingNotificationSubscriptionFailed);
  }, [isCreatingNotificationSubscriptionFailed]);

  useEffect(() => {
    if (userDetails) {
      setFirstName(userDetails?.FirstName);
      setMiddleName(userDetails?.MiddleName);
      setLastName(userDetails?.LastName);
      setPhoneNumber(userDetails?.PhoneNumber ?? '');
      setEmail(userDetails?.Email);
      setDefaultPrincipalUuid(userDetails?.defaultProfileUuid);
      setIsRegisteredUser(!!userDetails?.Email);
    }
  }, [userDetails]);

  const handleOpenModal = (modalType: ModalTypes) => {
    dispatch({ type: clearUserDetailsResult.type });
    setModalType(modalType);
    setOpenResetLoginInfoModal(true);
  };

  const handleCloseModal = () => {
    setOpenResetLoginInfoModal(false);
  };

  useEffect(() => {
    const isValidPhone = phoneNumber === '' || E164PhoneRegex.test(phoneNumber as string);

    if (!firstName || !lastName || !isValidPhone) {
      if (!isValidPhone) {
        setPhoneError('Invalid phone number!');
      } else {
        setPhoneError('');
      }
      setIsInvalidGeneralInfo(true);
    } else {
      setPhoneError('');
      setIsInvalidGeneralInfo(false);
    }
  }, [firstName, lastName, phoneNumber]);

  const updateGeneralInfo = () => {
    setIsUpdatingGeneralInfo(true);

    handleUpdate({
      firstName,
      middleName,
      lastName,
      phoneNumber,
    });
  };

  const cancelGeneralInfo = () => {
    setFirstName(userDetails?.FirstName);
    setLastName(userDetails?.LastName);
    setPhoneNumber(userDetails?.PhoneNumber ?? '');
    setPhoneError('');
    setIsInvalidGeneralInfo(false);
    setIsUpdatedGeneralInfo(false);
    setIsUpdatingGeneralInfo(false);
  };

  const updateDefaultPrincipalUuid = (uuid: string) => {
    setDefaultPrincipalUuid(uuid);
    setIsUpdatedPrincipal(true);
    handleUpdate({ defaultPrincipalUuid: uuid });
  };

  const handleUpdate = (payload: UpdateUserDetailsDto) => {
    dispatch(updateUserDetails(payload));
  };

  useEffect(() => {
    if (isUpdateUserDetailsSuccess) {
      setIsUpdatedPrincipal(false);
      setIsUpdatedGeneralInfo(false);
      setIsUpdatingGeneralInfo(false);
    }
    if (updateUserDetailsError) {
      setIsUpdatedPrincipal(false);
      setIsUpdatedGeneralInfo(false);
      setIsUpdatingGeneralInfo(false);
      setLocalError('Error saving information.');
      setShowToast(true);
    }
  }, [isUpdateUserDetailsSuccess, updateUserDetailsError]);

  return (
    <Box>
      <Box sx={{ background: '#ffffff', borderRadius: '24px', padding: '30px 30px 50px', marginTop: '30px' }}>
        {userDetails && (
          <Grid container direction="row" spacing={2.5}>
            <Grid item xs={12} lg={7}>
              <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: '20px' }}>
                <Box className={classes.headerContainer}>
                  <Typography
                    sx={{
                      fontSize: '20px',
                      lineHeight: '22px',
                      fontWeight: 600,
                      fontFamily: 'Work Sans',
                      color: '#ffffff',
                      marginLeft: '8px',
                    }}
                  >
                    {`General`}
                  </Typography>

                  {isRegisteredUser && (
                    <Box sx={{ marginRight: '4px' }}>
                      {isUpdatedGeneralInfo && !isUpdatingGeneralInfo && (
                        <FormControl sx={{ m: 0 }}>
                          <Button
                            variant="text"
                            sx={{
                              color: '#fff',
                              textTransform: 'capitalize',
                              borderRadius: '24px',
                              border: '2px solid #39A2DB',
                              width: '100px',
                              height: '31px',
                              mr: 1,
                            }}
                            onClick={cancelGeneralInfo}
                          >
                            <Typography
                              sx={{
                                fontSize: '14px',
                                lineHeight: '16px',
                                fontWeight: 400,
                                fontFamily: 'Work Sans',
                                color: '#39A2DB',
                              }}
                            >
                              {`Cancel`}
                            </Typography>
                          </Button>
                        </FormControl>
                      )}

                      <FormControl sx={{ m: 0 }}>
                        <Button
                          variant="contained"
                          onClick={updateGeneralInfo}
                          className={classes.saveButtonStyle}
                          sx={{ width: '100px', height: '31px' }}
                          disabled={!isUpdatedGeneralInfo || isInvalidGeneralInfo}
                        >
                          <Typography
                            sx={{
                              fontSize: '14px',
                              lineHeight: '16px',
                              fontWeight: 400,
                              fontFamily: 'Work Sans',
                              color: '#ffffff',
                              textTransform: 'capitalize !important',
                            }}
                          >
                            {isUpdatingGeneralInfo ? <CircularProgress size={13} sx={{ color: '#ffffff', mr: 0.5 }} /> : ''}
                            {`Save`}
                          </Typography>
                        </Button>
                      </FormControl>
                    </Box>
                  )}
                </Box>

                <Box className={classes.bodyContainer}>
                  <Grid container direction="row">
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      sx={{
                        borderRight: !matchesMobile ? '1px solid #F1F0F5' : 'none',
                        borderBottom: matchesMobile ? '1px solid #F1F0F5' : 'none',
                        height: '100%',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '18px',
                          lineHeight: '22px',
                          fontWeight: 400,
                          fontFamily: 'Work Sans',
                          color: '#000000',
                          margin: '10px 22px',
                        }}
                      >
                        {`Name`}
                      </Typography>

                      <Box sx={{ padding: '14px 14px 10px 27px' }}>
                        <FormControl fullWidth error={false}>
                          <TextField
                            id={'first_name'}
                            name={'first_name'}
                            value={firstName}
                            onChange={(e) => {
                              setIsUpdatedGeneralInfo(true);
                              setFirstName(e.target.value);
                            }}
                            label={'First Name'}
                            InputLabelProps={{
                              className: classes.placeholderStyle,
                            }}
                            InputProps={{
                              className: classes.inputStyle,
                            }}
                            error={!firstName}
                            disabled={!isRegisteredUser}
                          />
                        </FormControl>
                      </Box>

                      <Box sx={{ padding: '10px 14px 14px 27px' }}>
                        <FormControl fullWidth error={false}>
                          <TextField
                            id={'middle_name'}
                            name={'middle_name'}
                            value={middleName}
                            onChange={(e) => {
                              setIsUpdatedGeneralInfo(true);
                              setMiddleName(e.target.value);
                            }}
                            label={'Middle Name'}
                            InputLabelProps={{
                              className: classes.placeholderStyle,
                            }}
                            InputProps={{
                              className: classes.inputStyle,
                            }}
                            // error={!middleName}
                            disabled={!isRegisteredUser}
                          />
                        </FormControl>
                      </Box>

                      <Box sx={{ padding: '10px 14px 14px 27px' }}>
                        <FormControl fullWidth error={false}>
                          <TextField
                            id={'last_name'}
                            name={'last_name'}
                            value={lastName}
                            onChange={(e) => {
                              setIsUpdatedGeneralInfo(true);
                              setLastName(e.target.value);
                            }}
                            label={'Last Name'}
                            InputLabelProps={{
                              className: classes.placeholderStyle,
                            }}
                            InputProps={{
                              className: classes.inputStyle,
                            }}
                            error={!lastName}
                            disabled={!isRegisteredUser}
                          />
                        </FormControl>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        sx={{
                          fontSize: '18px',
                          lineHeight: '22px',
                          fontWeight: 400,
                          fontFamily: 'Work Sans',
                          color: '#000000',
                          margin: '10px 22px',
                        }}
                      >
                        {`Phone`}
                      </Typography>

                      <Box sx={{ padding: '14px 14px 14px 27px' }}>
                        {isRegisteredUser ? (
                          <PhoneNumberInput
                            value={phoneNumber}
                            showErrorMessage={!!phoneError}
                            errorMessage={phoneError}
                            onChange={(phone) => {
                              setIsUpdatedGeneralInfo(true);
                              setPhoneNumber(phone.startsWith('+') ? phone : `+${phone}`);
                            }}
                          />
                        ) : (
                          <Box className={classes.disabledInputContainer}></Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: matchesIpad ? '0' : '20px' }}>
                <Box className={classes.headerContainer}>
                  <Typography
                    sx={{
                      fontSize: '20px',
                      lineHeight: '22px',
                      fontWeight: 600,
                      fontFamily: 'Work Sans',
                      color: '#ffffff',
                      marginLeft: '8px',
                    }}
                  >
                    {`Log In Details`}
                  </Typography>
                </Box>

                <Box
                  className={classes.bodyContainer}
                  sx={{
                    padding: '10px 16px 10px 18px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-evenly',
                  }}
                >
                  <Grid container direction="row">
                    <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        sx={{
                          fontSize: '18px',
                          lineHeight: '22px',
                          fontWeight: 400,
                          fontFamily: 'Work Sans',
                          color: '#000000',
                          marginBottom: matchesMobile ? '10px' : '0',
                        }}
                      >
                        {`Email:`}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      {isRegisteredUser ? (
                        <Box className={classes.loginFieldContainer}>
                          <Typography
                            sx={{
                              fontSize: '16px',
                              lineHeight: '19px',
                              fontWeight: 400,
                              fontFamily: 'Work Sans',
                              color: '#14142B',
                              width: '100%',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {email}
                          </Typography>

                          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ position: 'absolute', right: '10px' }}>
                            <img src={EditPinIcon} alt="Edit property" style={{ cursor: 'pointer', width: 13, height: 17 }} onClick={() => handleOpenModal(ModalTypes.Email)} />
                          </Stack>
                        </Box>
                      ) : (
                        <Box className={classes.disabledLoginFieldContainer}></Box>
                      )}
                    </Grid>
                  </Grid>

                  <Grid container direction="row">
                    <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        sx={{
                          fontSize: '18px',
                          lineHeight: '22px',
                          fontWeight: 400,
                          fontFamily: 'Work Sans',
                          color: '#000000',
                          marginTop: matchesMobile ? '10px' : '0',
                          marginBottom: matchesMobile ? '10px' : '0',
                        }}
                      >
                        {`Password:`}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      {isRegisteredUser ? (
                        <>
                        <Box className={classes.loginFieldContainer}>
                          <Typography
                            sx={{
                              fontSize: '16px',
                              lineHeight: '19px',
                              fontWeight: 400,
                              fontFamily: 'Work Sans',
                              color: '#14142B',
                              width: '100%',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                            }}
                          >
                            {`************`}
                          </Typography>
                          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ position: 'absolute', right: '10px' }}>
                            <img src={EditPinIcon} alt="Edit property" style={{ cursor: 'pointer', width: 13, height: 17 }} onClick={() => handleOpenModal(ModalTypes.Password)} />
                          </Stack>
                        </Box>
                        {/* <Box>
                          <img src={URL.createObjectURL(new Blob([toptQr], { type: "image/svg+xml" }))} style={{ width: '244px', height: '244px' }} alt="TOTP_QR" />
                        </Box> */}
                        </>
                      ) : (
                        <Box className={classes.disabledLoginFieldContainer}></Box>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>
            {/* Only show this section when the user is an owner or a pm impersonating an Owner, in the future it might be its own thing */}
            {(currentPrincipal?.role === Role.Owner) && (
              <Grid item xs={12} sx={{ mt: 3.5 }}>
                <Box sx={{ minHeight: '25vh', width: '100%' }}>
                  <Box className={classes.headerContainer}>
                    <Typography
                      sx={{
                        fontSize: '20px',
                        lineHeight: '22px',
                        fontWeight: 600,
                        fontFamily: 'Work Sans',
                        color: '#ffffff',
                        marginLeft: '8px',
                      }}
                    >
                      {`Contact Preferences`}
                    </Typography>
                  </Box>
                  <Box className={classes.bodyContainer}>
                    {supportedNotifications &&
                      supportedNotifications.map((report, index) => {
                        return <NotificationTab key={index} {...report} />;
                      })}
                  </Box>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box className={classes.headerContainer}>
                  <Typography
                    sx={{
                      fontSize: '20px',
                      lineHeight: '22px',
                      fontWeight: 600,
                      fontFamily: 'Work Sans',
                      color: '#ffffff',
                      marginLeft: '8px',
                    }}
                  >
                    {`Manage Accounts`}
                  </Typography>

                  {isRegisteredUser && (
                    <Link href="/residentpage" target="_blank">
                      <IconButton sx={{ p: 0.5 }}>
                        <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <img src={AddPlusIcon} style={{ width: '14px', height: '14px' }} alt="Add Account" />
                        </Icon>
                      </IconButton>
                    </Link>
                  )}
                </Box>

                <Box className={classes.bodyContainer} sx={{ display: 'flex', flexDirection: 'column', minHeight: '300px', paddingBottom: '50px' }}>
                  {userDetails?.Profiles?.map((res: any) => (
                    <Box key={res.Uuid} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #EEEEEE', minHeight: '50px' }}>
                      <Typography
                        sx={{
                          fontSize: '18px',
                          lineHeight: '22px',
                          fontWeight: 400,
                          fontFamily: 'Work Sans',
                          color: '#14142B',
                          marginLeft: matchesMobile ? '10px' : '31px',
                        }}
                      >
                        {res.DisplayName}
                      </Typography>

                      {isRegisteredUser && (
                        <span
                          style={{
                            cursor:
                              res.Uuid === userDetails.defaultProfileUuid
                                ? 'default'
                                : res.Uuid === defaultPrincipalUuid && isUpdatedPrincipal
                                ? 'default'
                                : res.Uuid !== defaultPrincipalUuid && isUpdatedPrincipal
                                ? 'not-allowed'
                                : res.Uuid === defaultPrincipalUuid && !isUpdatedPrincipal
                                ? 'default'
                                : 'pointer',
                            fontSize: '16px',
                            lineHeight: '22px',
                            color: res.Uuid !== defaultPrincipalUuid && res.Uuid !== userDetails.defaultProfileUuid ? '#39A2DB' : '#A8A8A8',
                            marginRight: matchesMobile ? '10px' : '21px',
                            textAlign: 'right',
                          }}
                          onClick={() => {
                            if (res.Uuid !== defaultPrincipalUuid && !isUpdatedPrincipal) updateDefaultPrincipalUuid(res.Uuid);
                          }}
                        >
                          {res.Uuid === defaultPrincipalUuid && isUpdatedPrincipal ? (
                            <CircularProgress size={16} sx={{ mr: 0.5 }} />
                          ) : res.Uuid === userDetails.defaultProfileUuid ? (
                            'Default'
                          ) : (
                            'Make Default'
                          )}
                        </span>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
      {/* error alert both for validations and failed api calls */}
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={showToast} autoHideDuration={4000} onClose={() => setShowToast(false)}>
        <Alert
          onClose={() => {
            setShowToast(false);
          }}
          severity="error"
        >
          {localError}
        </Alert>
      </Snackbar>
      {openResetLoginInfoModal ? <ResetLoginInfoModal open={openResetLoginInfoModal} handleClose={handleCloseModal} modalType={modalType} /> : <></>}
    </Box>
  );
}
